import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import { authService } from "../services/authService";
import { FaUserPlus, FaTimes } from "react-icons/fa";

/**
 * @file CreateUserModal.js
 * @description Modal for creating a new user (Admin/Tenant).
 */
export default function CreateUserModal({ show, onClose, onSuccess }) {
    const [formData, setFormData] = useState({
        username: "",
        email: "",
        password: "",
        first_name: "",
        last_name: "",
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");

        if (!formData.username || !formData.email || !formData.password || !formData.first_name) {
            setError("Please fill in all required fields.");
            return;
        }

        setLoading(true);
        try {
            await authService.createUser(formData);
            if (onSuccess) onSuccess();
            handleClose();
        } catch (err) {
            setError(err.message || "Failed to create user.");
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setFormData({
            username: "",
            email: "",
            password: "",
            first_name: "",
            last_name: ""
        });
        setError("");
        onClose();
    };

    return (
        <Modal show={show} onHide={handleClose} centered backdrop="static" className="fade-modal">
            <Modal.Body className="p-0 border-0 rounded-4 shadow-lg overflow-hidden">
                <div className="d-flex">
                    {/* Left Side - Visual/Info (Optional, for "smooth" layout) */}
                    <div className="bg-primary p-4 d-none d-md-flex flex-column justify-content-center align-items-center text-white text-center" style={{ width: "35%", minHeight: "500px" }}>
                        <div className="bg-white bg-opacity-25 rounded-circle d-flex align-items-center justify-content-center mb-3" style={{ width: 80, height: 80 }}>
                            <FaUserPlus size={32} />
                        </div>
                        <h4 className="fw-bold mb-2">New Admin</h4>
                        <p className="small opacity-75">Create a         to manage tenants and resources.</p>
                    </div>

                    {/* Right Side - Form */}
                    <div className="p-4 p-lg-5 bg-white flex-grow-1">
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <h4 className="fw-bold m-0 text-dark">Create User</h4>
                            <button className="btn btn-link text-muted p-0" onClick={handleClose}>
                                <FaTimes size={18} />
                            </button>
                        </div>

                        {error && (
                            <div className="alert alert-danger py-2 small border-0 bg-danger-subtle text-danger mb-4">
                                {error}
                            </div>
                        )}

                        <form onSubmit={handleSubmit}>
                            <div className="row g-3">
                                <div className="col-md-6">
                                    <label className="form-label small fw-bold text-secondary">First Name *</label>
                                    <input
                                        type="text"
                                        className="form-control bg-light border-0 py-2"
                                        name="first_name"
                                        value={formData.first_name}
                                        onChange={handleChange}
                                        placeholder="Jane"
                                    />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label small fw-bold text-secondary">Last Name</label>
                                    <input
                                        type="text"
                                        className="form-control bg-light border-0 py-2"
                                        name="last_name"
                                        value={formData.last_name}
                                        onChange={handleChange}
                                        placeholder="Doe"
                                    />
                                </div>
                                <div className="col-12">
                                    <label className="form-label small fw-bold text-secondary">Username *</label>
                                    <input
                                        type="text"
                                        className="form-control bg-light border-0 py-2"
                                        name="username"
                                        value={formData.username}
                                        onChange={handleChange}
                                        placeholder="janedoe"
                                    />
                                </div>
                                <div className="col-12">
                                    <label className="form-label small fw-bold text-secondary">Email Address *</label>
                                    <input
                                        type="email"
                                        className="form-control bg-light border-0 py-2"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        placeholder="jane@example.com"
                                    />
                                </div>
                                <div className="col-12">
                                    <label className="form-label small fw-bold text-secondary">Password *</label>
                                    <input
                                        type="password"
                                        className="form-control bg-light border-0 py-2"
                                        name="password"
                                        value={formData.password}
                                        onChange={handleChange}
                                        placeholder="••••••••"
                                    />
                                </div>
                            </div>

                            <div className="d-flex justify-content-end gap-2 mt-5">
                                <button type="button" className="btn btn-light text-secondary border-0 px-4" onClick={handleClose}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-primary px-4 fw-bold shadow-sm" disabled={loading}>
                                    {loading ? "Creating..." : "Create User"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    );
}
